import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";

// Get token from sessionStorage for authentication
const getAuthToken = () => {
  const auth: any = sessionStorage.getItem("authState");
  if (!auth) {
    console.error("No authState found in sessionStorage");
    return null;
  }
  try {
    const jsonData = JSON.parse(auth);
    return jsonData?.token;
  } catch (error) {
    console.error("Error parsing authState:", error);
    return null;
  }
};

// TypeScript interfaces for Broadcast
export interface BroadcastRecipient {
  _id: string;
  businessInformation: {
    businessName: string;
  };
}

export interface BroadcastLog {
  action: string;
  details: {
    recipientCount: number;
    isAll: boolean;
    title: string;
  };
  _id: string;
  createdAt: string;
}

export interface Broadcast {
  _id: string;
  issueType: string;
  title: string;
  description: string;
  recipients: string[] | BroadcastRecipient[];
  isAll: boolean;
  logs: BroadcastLog[];
  createdAt: string;
  updatedAt: string;
  __v?: number;
}

export interface CreateBroadcastPayload {
  issueType: string;
  title: string;
  description: string;
  isAll: boolean;
  recipients?: string[];
}

export interface CreateBroadcastResponse {
  message: string;
  broadcast: Broadcast;
}

// Redux state interface
interface RestaurantCommunicationState {
  broadcasts: Broadcast[];
  currentBroadcast: Broadcast | null;
  status: "idle" | "loading" | "success" | "failed";
  createStatus: "idle" | "loading" | "success" | "failed";
  detailsStatus: "idle" | "loading" | "success" | "failed";
  error: string | null;
  total: number;
}

const initialState: RestaurantCommunicationState = {
  broadcasts: [],
  currentBroadcast: null,
  status: "idle",
  createStatus: "idle",
  detailsStatus: "idle",
  error: null,
  total: 0,
};

// Async thunks for API calls

// Create Broadcast
export const createBroadcast = createAsyncThunk<
  CreateBroadcastResponse,
  CreateBroadcastPayload,
  { rejectValue: string }
>(
  "restaurantCommunication/createBroadcast",
  async (payload, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.post<CreateBroadcastResponse>(
        `${process.env.REACT_APP_RESTAURANT_URL}/broadcast`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to create broadcast"
      );
    }
  }
);

// Get All Broadcasts
export const getAllBroadcasts = createAsyncThunk<
  Broadcast[],
  void,
  { rejectValue: string }
>(
  "restaurantCommunication/getAllBroadcasts",
  async (_, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<Broadcast[]>(
        `${process.env.REACT_APP_RESTAURANT_URL}/broadcast`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      console.error("getAllBroadcasts error:", error.response || error);
      console.error("Error details:", {
        status: error.response?.status,
        statusText: error.response?.statusText,
        data: error.response?.data,
        headers: error.response?.headers,
        config: {
          url: error.config?.url,
          method: error.config?.method,
          headers: error.config?.headers,
        },
      });
      return rejectWithValue(
        error.response?.data?.message ||
          error.response?.data?.error ||
          "Failed to fetch broadcasts"
      );
    }
  }
);

// Get Single Broadcast
export const getBroadcastById = createAsyncThunk<
  Broadcast,
  string,
  { rejectValue: string }
>(
  "restaurantCommunication/getBroadcastById",
  async (broadcastId, { rejectWithValue }) => {
    try {
      const token = getAuthToken();
      if (!token) {
        return rejectWithValue("Authentication token not found");
      }

      const response = await axios.get<Broadcast>(
        `${process.env.REACT_APP_RESTAURANT_URL}/broadcast/${broadcastId}`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );
      return response.data;
    } catch (error: any) {
      return rejectWithValue(
        error.response?.data?.message || "Failed to fetch broadcast details"
      );
    }
  }
);

// Redux slice
const restaurantCommunicationSlice = createSlice({
  name: "restaurantCommunication",
  initialState,
  reducers: {
    clearCurrentBroadcast: (state) => {
      state.currentBroadcast = null;
      state.detailsStatus = "idle";
    },
    clearErrors: (state) => {
      state.error = null;
    },
    resetCreateStatus: (state) => {
      state.createStatus = "idle";
    },
  },
  extraReducers: (builder) => {
    builder
      // Create Broadcast
      .addCase(createBroadcast.pending, (state) => {
        state.createStatus = "loading";
        state.error = null;
      })
      .addCase(createBroadcast.fulfilled, (state, action) => {
        state.createStatus = "success";
        state.broadcasts.unshift(action.payload.broadcast);
        state.total += 1;
        state.error = null;
      })
      .addCase(createBroadcast.rejected, (state, action) => {
        state.createStatus = "failed";
        state.error = action.payload || "Failed to create broadcast";
      })

      // Get All Broadcasts
      .addCase(getAllBroadcasts.pending, (state) => {
        state.status = "loading";
        state.error = null;
      })
      .addCase(getAllBroadcasts.fulfilled, (state, action) => {
        state.status = "success";
        state.broadcasts = action.payload;
        state.total = action.payload.length;
        state.error = null;
      })
      .addCase(getAllBroadcasts.rejected, (state, action) => {
        state.status = "failed";
        state.error = action.payload || "Failed to fetch broadcasts";
      })

      // Get Broadcast by ID
      .addCase(getBroadcastById.pending, (state) => {
        state.detailsStatus = "loading";
        state.error = null;
      })
      .addCase(getBroadcastById.fulfilled, (state, action) => {
        state.detailsStatus = "success";
        state.currentBroadcast = action.payload;
        state.error = null;
      })
      .addCase(getBroadcastById.rejected, (state, action) => {
        state.detailsStatus = "failed";
        state.error = action.payload || "Failed to fetch broadcast details";
      });
  },
});

export const { clearCurrentBroadcast, clearErrors, resetCreateStatus } =
  restaurantCommunicationSlice.actions;
export default restaurantCommunicationSlice.reducer;
